/**
 * ViewerJoinPage — landing page at /join where viewer devices type their
 * group's short code and land in the live-discussion view for that group.
 *
 * No name, no mic, no audio. The server signs a 12h viewer token that
 * the viewer page uses for all subsequent reads + speaker-tag edits.
 * Scoped per session_device so a viewer in one group can't read another.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function ViewerJoinPage() {
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // StrictMode double-invokes effects in dev; guard so we don't fire two
    // join_viewer POSTs from the same URL hit.
    const autoSubmittedRef = useRef(false);

    // Support pre-filled code from URL OR sessionStorage. cs330 students
    // arriving from StudentRightPanel get the code via sessionStorage so
    // it doesn't end up in the address bar; legacy ?code= still works.
    // When pre-filled, auto-submit so they land directly in the live viewer
    // view without a second click here.
    useEffect(() => {
        let pre = searchParams.get('code');
        if (!pre) {
            try {
                pre = sessionStorage.getItem('cs330_pending_viewer_code');
                if (pre) sessionStorage.removeItem('cs330_pending_viewer_code');
            } catch (_) {}
        }
        if (pre && /^[0-9A-Za-z]{3,6}$/.test(pre)) {
            setCode(pre);
            if (!autoSubmittedRef.current) {
                autoSubmittedRef.current = true;
                submit(null, pre);
            }
        }
    }, [searchParams]);

    const submit = async (e, codeOverride) => {
        e?.preventDefault?.();
        const clean = (codeOverride ?? code).trim();
        if (!clean) {
            setError('Please enter the group code.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const res = await fetch('/api/v1/groups/join_viewer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: clean }),
                credentials: 'include',
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.message || `Could not join (HTTP ${res.status})`);
            }
            const body = await res.json();
            // Stash the token + context so the viewer page can authenticate
            // its reads. sessionStorage survives reload within the tab but
            // not cross-tab, which is the right isolation for this use.
            sessionStorage.setItem('byod_viewer_token', body.viewer_token);
            sessionStorage.setItem('byod_viewer_session_device_id', String(body.session_device_id));
            sessionStorage.setItem('byod_viewer_session_id', String(body.session_id));
            sessionStorage.setItem('byod_viewer_recorder_name', body.recorder_name || '');
            navigate(`/viewer/${body.session_device_id}`);
        } catch (err) {
            setError(err.message || 'Failed to join');
        } finally {
            setLoading(false);
        }
    };

    return (
        // #root is a flex-row container with overflow-clip (see index.css)
        // — need explicit flex/width so this page fills its slot instead
        // of collapsing to min-content.
        <div style={{
            flex: 1, width: '100%', minHeight: '100vh',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#f3f4f6', padding: 20,
        }}>
            <div style={{
                width: '100%', maxWidth: 420, background: '#fff',
                borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
                padding: '32px 28px',
            }}>
                <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 600, color: '#111827' }}>
                    Join your group
                </h1>
                <p style={{ margin: '8px 0 24px', fontSize: '0.9rem', color: '#6b7280' }}>
                    Type the 4-digit code shown on your group's recording laptop.
                    You'll see the live transcript and can review the summary afterward.
                </p>
                <form onSubmit={submit}>
                    <input
                        type="text"
                        inputMode="numeric"
                        autoFocus
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Za-z]/g, '').slice(0, 8))}
                        placeholder="4281"
                        style={{
                            width: '100%', padding: '14px 16px', fontSize: '1.6rem',
                            textAlign: 'center', letterSpacing: '0.3em',
                            border: '2px solid #d1d5db', borderRadius: 8,
                            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                            boxSizing: 'border-box',
                        }}
                    />
                    {error && (
                        <div style={{
                            marginTop: 12, padding: '8px 12px', borderRadius: 6,
                            background: '#fee2e2', color: '#991b1b', fontSize: '0.85rem',
                        }}>
                            {error}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={loading || !code.trim()}
                        style={{
                            marginTop: 20, width: '100%', padding: '12px',
                            border: 'none', borderRadius: 8,
                            background: loading || !code.trim() ? '#9ca3af' : '#2563eb',
                            color: '#fff', fontSize: '1rem', fontWeight: 600,
                            cursor: loading || !code.trim() ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {loading ? 'Joining…' : 'Join'}
                    </button>
                </form>
                <p style={{ margin: '20px 0 0', fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center' }}>
                    No account needed. One member of your group records audio;
                    everyone else uses this page.
                </p>
            </div>
        </div>
    );
}
